import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  modelSupportsAudio,
  modelSupportsFiles,
  modelSupportsImages,
  modelSupportsReasoning,
  modelSupportsVideo
} from '../utils/modelHelpers';

const ModelContext = createContext({
  models: [],
  loading: true,
  error: null,
  defaultModel: null,
  getModelByModelString: () => null,
  getModelByName: () => null
});

const addUiCapabilities = (model) => ({
  ...model,
  capabilities: {
    images: modelSupportsImages(model),
    files: modelSupportsFiles(model),
    audio: modelSupportsAudio(model),
    video: modelSupportsVideo(model),
    reasoning: modelSupportsReasoning(model)
  }
});

const fallbackModels = [];

const normalizeModel = (data = {}, id) => {
  const supportedInputs = Array.isArray(data.supportedInputs) ? data.supportedInputs : [];
  const capabilityMap = data.capabilities || {};
  const thinkingFlag = data.thinking === true;
  const deepResearchFlag = data.deep_research === true || data.deepResearch === true || capabilityMap.deepResearch === true;
  const reasoningLevels = Array.isArray(data.reasoningLevels) ? data.reasoningLevels : undefined;
  
  return {
    name: data.name || data.modelName || id,
    modelString: data.modelString || id,
    provider: data.provider || 'OpenAI',
    // Optional UI-only grouping. Keep `provider` stable because legacy clients use
    // it to choose the backend endpoint (for example, Meta routes to Groq).
    displayProvider: data.displayProvider || data.provider || 'OpenAI',
    subtitle: data.subtitle || '',
    description: data.description || data.desc || '',
    symbol: data.symbol || 'sparkles',
    color: data.color || '#4b5563',
    maxTokens: data.maxTokens ?? data.max_tokens ?? null,
    messageLimit: data.messageLimit ?? data.message_limit ?? 0,
    isPremium: data.isPremium ?? false,
    supportedInputs,
    isNew: data.isNew ?? false,
    isOld: data.isOld ?? false,
    thinking: thinkingFlag,
    online: data.online ?? false,
    deep_research: deepResearchFlag,
    // Match the Apple clients: the catalog's legacy `thinking` capability also
    // identifies models that should send reasoning effort to the backend.
    isReasoning:
      data.isReasoning ??
      capabilityMap.reasoning ??
      (thinkingFlag || Boolean(reasoningLevels?.length)),
    reasoningLevels,
    status: data.status || 'active',
    platforms: data.platforms || ['web'],
    sortOrder: data.sortOrder ?? 0
  };
};

export const ModelProvider = ({ children, platform = 'web' }) => {
  const [models, setModels] = useState(fallbackModels);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(
      collection(firestore, 'models'),
      where('status', '==', 'active'),
      where('platforms', 'array-contains', platform),
      orderBy('sortOrder', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const remoteModels = snapshot.docs.map((doc) =>
        addUiCapabilities(normalizeModel(doc.data(), doc.id))
      );

      if (remoteModels.length > 0) {
        setModels(remoteModels);
      } else {
        setModels(fallbackModels);
      }
      setLoading(false);
    }, (err) => {
      console.error('Error loading models from Firestore', err);
      setError(err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [platform]);

  const modelsByString = useMemo(() => {
    const map = {};
    models.forEach((model) => {
      map[model.modelString] = model;
    });
    return map;
  }, [models]);

  const getModelByModelString = useCallback(
    (modelString) => modelsByString[modelString] || null,
    [modelsByString]
  );

  const getModelByName = useCallback(
    (name) => models.find((model) => model.name === name) || null,
    [models]
  );

  const getMaxTokens = useCallback(
    (modelString) => {
      const model = getModelByModelString(modelString);
      return model?.maxTokens ?? 4000;
    },
    [getModelByModelString]
  );

  const defaultModel = useMemo(() => models[0] || null, [models]);

  return (
    <ModelContext.Provider
      value={{
        models,
        loading,
        error,
        defaultModel,
        getModelByModelString,
        getModelByName,
        getMaxTokens
      }}
    >
      {children}
    </ModelContext.Provider>
  );
};

export const useModels = () => useContext(ModelContext);
